// Tab Comunicado — estadística "certificada" para difusión pública.
//
// A diferencia del resto del dashboard, acá la prioridad es la PRECISIÓN: los
// números están pensados para que el Fiscal los cite en un comunicado. Por eso:
//   - Se excluyen las causas con fecha basura (NULL, "1900", futuras) y se
//     muestran aparte para que el total cuadre y sea auditable.
//   - El "recaudado" es CAJA REAL (cobrado vía planes DGR), NO monto demandado.
//   - "Finalizadas" es un conteo: el estado no dice si fue a favor o en contra.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert, Chip, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { dashEjecComunicadoJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');

// Detecta años con conteo anómalo: muy por debajo de la mediana de sus vecinos.
// Solo es una marca visual de "revisar" — no excluye nada.
function marcarAnomalias(porAnio) {
    if (!porAnio || porAnio.length < 3) return new Set();
    const counts = porAnio.map(r => r.causas).slice().sort((a, b) => a - b);
    const mediana = counts[Math.floor(counts.length / 2)] || 0;
    const sospechosos = new Set();
    porAnio.forEach(r => {
        // Marca años (no el actual/futuro parcial) con < 25% de la mediana.
        if (mediana > 0 && r.causas < mediana * 0.25) sospechosos.add(r.anio);
    });
    return sospechosos;
}

export default function TabComunicado({ desde, hasta, historico: historicoProp, onHistoricoChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // Modo controlado por el padre (DashboardEjecutivo) para que el export a PDF
    // respete el mismo alcance. Fallback local si se usa el tab sin props.
    // 'historico' = sin filtro (todo 1990..hoy) · 'periodo' = usa el período del header.
    const [historicoLocal, setHistoricoLocal] = useState(true);
    const historico = historicoProp != null ? historicoProp : historicoLocal;
    const modo = historico ? 'historico' : 'periodo';
    const setModo = (v) => {
        const esHist = v === 'historico';
        setHistoricoLocal(esHist);
        onHistoricoChange?.(esHist);
    };

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        const fetchData = modo === 'periodo' && desde && hasta
            ? dashEjecComunicadoJWT(desde, hasta)
            : dashEjecComunicadoJWT();
        fetchData
            .then(r => { if (!cancel && r?.ok) setData(r.data); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [modo, desde, hasta]);

    if (loading) {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[1, 2, 3, 4].map(i => (<Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}><Skeleton variant="rounded" height={120} sx={{ borderRadius: `${BR}px` }} /></Grid>))}
                </Grid>
                <Skeleton variant="rounded" height={400} sx={{ borderRadius: `${BR}px` }} />
            </Box>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    const c = data.causas || {};
    const md = data.montoDemandado || {};
    const rec = data.recaudado;
    const rol = data.porRol;
    const ex = data.excluidos || {};
    const totalExcluidos = (ex.sinFecha || 0) + (ex.centinela || 0) + (ex.futuras || 0);
    const anomalias = marcarAnomalias(data.porAnio || []);

    const rangoTxt = data.filtroAplicado
        ? `${data.filtroAplicado.desde} a ${data.filtroAplicado.hasta}`
        : 'histórico completo (desde 1990)';

    // Tarjeta KPI reutilizable.
    const KpiCard = ({ titulo, valor, sub, grad, icon }) => (
        <Paper sx={{ p: 2, borderRadius: `${BR}px`, background: grad, color: 'white', height: '100%' }}>
            <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {icon} {titulo}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{valor}</Typography>
            {sub && <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.5 }}>{sub}</Typography>}
        </Paper>
    );

    return (
        <Box>
            {/* Control de alcance + leyenda de criterio */}
            <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase' }}>
                    Alcance de los números:
                </Typography>
                <ToggleButtonGroup size="small" exclusive value={modo} onChange={(_, v) => v && setModo(v)}>
                    <ToggleButton value="historico" sx={{ textTransform: 'none' }}>Histórico completo</ToggleButton>
                    <ToggleButton value="periodo" sx={{ textTransform: 'none' }}>Usar período del filtro</ToggleButton>
                </ToggleButtonGroup>
                <Chip size="small" label={`Rango: ${rangoTxt}`} sx={{ bgcolor: '#e3f2fd', color: PRIMARY, fontWeight: 600 }} />
                {modo === 'periodo' && (!desde || !hasta) && (
                    <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                        Elegí un período en el filtro de arriba.
                    </Typography>
                )}
            </Paper>

            {/* CAUSAS */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>Cantidad de causas (judiciales)</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard titulo="Causas totales" icon="📂" valor={fmtNum(c.total)}
                        grad={`linear-gradient(135deg, ${PRIMARY} 0%, #42a5f5 100%)`}
                        sub="con fecha válida en el rango" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard titulo="En trámite" icon="⚖️" valor={fmtNum(c.enTramite)}
                        grad="linear-gradient(135deg, #00695c 0%, #26a69a 100%)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard titulo="En plan de pago" icon="💳" valor={fmtNum(c.enPlanPago)}
                        grad="linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <KpiCard titulo="Finalizadas" icon="🏁" valor={fmtNum(c.finalizadas)}
                        grad="linear-gradient(135deg, #546e7a 0%, #90a4ae 100%)"
                        sub="conteo — no implica cobro" />
                </Grid>
            </Grid>

            {/* MONTOS */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>Montos</Typography>
            <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard titulo="Demandado en trámite" icon="📊" valor={fmtMoney(md.enTramite)}
                        grad="linear-gradient(135deg, #c62828 0%, #ef5350 100%)"
                        sub="monto nominal reclamado" />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <KpiCard titulo="Demandado en planes de pago" icon="💰" valor={fmtMoney(md.enPlanPago)}
                        grad="linear-gradient(135deg, #ef6c00 0%, #ffa726 100%)" />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    {rec ? (
                        <KpiCard titulo="Recaudado (caja real DGR)" icon="✅" valor={fmtMoney(rec.total)}
                            grad="linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)"
                            sub={`${fmtNum(rec.certificados)} certificados con pago registrado`} />
                    ) : (
                        <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%', bgcolor: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                El recaudado es provincial y no se desglosa por área.
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            {/* Nota de criterio sobre los montos */}
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: `${BR}px` }}>
                <Typography variant="caption" component="div">
                    <strong>Demandado</strong> = monto nominal reclamado en los certificados (al momento de iniciar la causa).
                    Como es nominal histórico, los años recientes pesan mucho más por inflación — mirá el desglose por año.
                    <br />
                    <strong>Recaudado</strong> = capital efectivamente cobrado vía planes de pago de DGR (Rentas), por fecha de pago real.
                    No incluye demandas cobradas por fuera de DGR (el sistema no registra ese monto como caja).
                </Typography>
            </Alert>

            {/* ROL DE LA PROVINCIA (causas en trámite) */}
            {rol && (
                <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
                        Rol de la Provincia <Typography component="span" variant="caption" sx={{ color: '#888' }}>(causas activas / no finalizadas)</Typography>
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard titulo="Como Actora" icon="🟢" valor={fmtNum(rol.actor)}
                                grad="linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)"
                                sub={rol.totalActivas ? `${((rol.actor / rol.totalActivas) * 100).toFixed(1)}% de las causas activas` : null} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard titulo="Como Demandada" icon="🔵" valor={fmtNum(rol.demandado)}
                                grad="linear-gradient(135deg, #6a1b9a 0%, #ab47bc 100%)"
                                sub="la Provincia es demandada" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard titulo="Al cobro (actora)" icon="📈" valor={fmtMoney(rol.alCobroActor)}
                                grad="linear-gradient(135deg, #c62828 0%, #ef5350 100%)"
                                sub="demandado de causas actor activas" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <KpiCard titulo="Ya cobrado (DGR)" icon="✅"
                                valor={rol.yaCobrado != null ? fmtMoney(rol.yaCobrado) : '—'}
                                grad="linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)"
                                sub={rol.yaCobrado != null ? 'caja real cobrada vía DGR' : 'provincial — no por área'} />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                        Rol detectado por carátula ("PROVINCIA DE SAN JUAN C/…" = actora; "… C/ PROVINCIA DE SAN JUAN" = demandada)
                        combinado con el carácter de parte. "Activa" = no finalizada. Las ejecuciones fiscales son casi todas actora;
                        la defensa del Estado (demandada) se concentra en pocos módulos. "Al cobro" es monto demandado nominal;
                        "ya cobrado" es caja real (no son restables entre sí por base temporal e inflación distintas).
                    </Typography>

                    {/* Desglose por módulo */}
                    {(rol.porModulo || []).length > 0 && (
                        <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                                Por módulo (causas activas)
                            </Typography>
                            <TableContainer sx={{ maxHeight: 420 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Módulo</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Como Actora</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Como Demandada</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rol.porModulo.map((mod, i) => (
                                            <TableRow key={i} hover>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>{mod.modulo}</TableCell>
                                                <TableCell align="right" sx={{ color: '#1565c0', fontWeight: 600 }}>{fmtNum(mod.actor)}</TableCell>
                                                <TableCell align="right" sx={{ color: '#6a1b9a', fontWeight: 600 }}>{fmtNum(mod.demandado)}</TableCell>
                                                <TableCell align="right">{fmtNum(mod.total)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ bgcolor: '#eef3f8' }}>
                                            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>{fmtNum(rol.actor)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: '#6a1b9a' }}>{fmtNum(rol.demandado)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(rol.totalActivas)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
                                Una causa puede figurar como actora y demandada a la vez (raro), por eso actora + demandada puede no dar exacto el total.
                            </Typography>
                        </Paper>
                    )}
                </>
            )}

            {/* Registros excluidos (transparencia) */}
            {totalExcluidos > 0 && (
                <Paper sx={{ p: 1.5, borderRadius: `${BR}px`, mb: 2, border: '1px solid #ffe0b2', bgcolor: '#fff8e1' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <WarningAmberIcon sx={{ color: '#f57c00' }} fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fmtNum(totalExcluidos)} causas excluidas por fecha no confiable:
                        </Typography>
                        <Chip size="small" label={`Sin fecha: ${fmtNum(ex.sinFecha)}`} />
                        <Chip size="small" label={`Fecha centinela (<1990, ej. "1900"): ${fmtNum(ex.centinela)}`} />
                        <Chip size="small" label={`Fechas futuras: ${fmtNum(ex.futuras)}`} />
                        <Typography variant="caption" sx={{ color: '#888', width: '100%', mt: 0.5 }}>
                            No se suman a los totales para que las cifras sean fiables. Se reportan acá para que el total cuadre.
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* Desglose por año — el corazón del análisis de fiabilidad */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                    Desglose por año de inicio
                    <Tooltip title="Los años marcados ⚠ tienen un conteo muy por debajo del resto — posible hueco de migración. Revisalos antes de citarlos.">
                        <InfoOutlinedIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', color: '#999' }} />
                    </Tooltip>
                </Typography>
                <TableContainer sx={{ maxHeight: 480 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Año</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Causas</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>En trámite</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Finalizadas</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Demandado en trámite</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data.porAnio || []).slice().reverse().map((r) => {
                                const sosp = anomalias.has(r.anio);
                                return (
                                    <TableRow key={r.anio} hover sx={sosp ? { bgcolor: '#fff8e1' } : undefined}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {sosp && <Tooltip title="Conteo anómalamente bajo — revisar"><span>⚠ </span></Tooltip>}
                                            {r.anio}
                                        </TableCell>
                                        <TableCell align="right">{fmtNum(r.causas)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.enTramite)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.finalizadas)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#c62828' }}>{fmtMoney(r.montoEnTramite)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Recaudado DGR por año */}
            {rec && (rec.porAnio || []).length > 0 && (
                <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#2e7d32' }}>
                        Recaudado vía planes DGR por año de pago
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                        Capital cobrado, deduplicado por plan y certificado. Fuente: Rentas (WSDGR).
                    </Typography>
                    <TableContainer sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Año de pago</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Certificados</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Recaudado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(rec.porAnio || []).slice().reverse().map((r) => (
                                    <TableRow key={r.anio} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.anio}</TableCell>
                                        <TableCell align="right">{fmtNum(r.certificados)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>{fmtMoney(r.recaudado)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(rec.certificados)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>{fmtMoney(rec.total)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ color: '#888' }}>
                        Nota: los datos de DGR arrancan en 2017 (inicio de la integración con Rentas).
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}
